import { FC } from 'react';
import { ArrowRight } from 'lucide-react';
import { useNavigate } from '@tanstack/react-router';
import { CourseWithProgress } from '@teachur/http-client';
import { useCourseSnapshot } from '../../hooks/useCourseSnapshot';
import { SnapshotTone } from '../../lib/courseSnapshot';

interface ContinueLearningPanelProps {
  course: CourseWithProgress;
}

interface SnapshotStatProps {
  label: string;
  value: string;
  unit?: string;
  tone?: SnapshotTone;
}

const TONE_CLASSES: Record<SnapshotTone, string> = {
  success: 'bold text-(--success-S700)',
  warning: 'bold text-(--warning-W700)',
  danger: 'bold text-(--danger-D700)',
  muted: 'text-(--neutral-black-NB500)',
};

const SnapshotStat: FC<SnapshotStatProps> = ({ label, value, unit, tone }) => (
  <div className="flex items-center justify-between gap-2 rounded-md border border-(--secondary-B300) bg-(--neutral-white-NW1000) px-2.5 py-2.5">
    <span className="text-[12px] font-semibold text-(--neutral-black-NB900)">{label}</span>
    <span className="text-[12px] font-medium text-(--neutral-black-NB600)">
      <span className={tone ? TONE_CLASSES[tone] : 'text-(--neutral-black-NB900)'}>{value}</span>
      {unit}
    </span>
  </div>
);

export const ContinueLearningPanel: FC<ContinueLearningPanelProps> = ({ course }) => {
  const navigate = useNavigate();
  const snapshot = useCourseSnapshot(course);

  const onContinue = () => {
    navigate({ to: `/courses/${course.id}` });
  };

  return (
    <div className="flex flex-col bg-(--primary-O100) sm:flex-row">
      <div className="flex min-w-0 flex-1 flex-col gap-4 px-5 py-6 sm:px-7">
        <div className="flex flex-col gap-1.5">
          <p className="bold m-0 [font-family:var(--font-montserrat)] text-[9px] tracking-[1.8px] text-[#7e8b96] uppercase">
            Continue learning
          </p>
          <h3 className="m-0! text-xl! bold text-(--neutral-black-NB900)!">{course.name}</h3>
          {course.shortDescription && (
            <p className="l5 regular m-0 line-clamp-2 text-(--neutral-black-NB900)">
              {course.shortDescription}
            </p>
          )}
        </div>

        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <SnapshotStat
            label="Objectives completed"
            value={String(snapshot.objectivesCompleted)}
            unit={`/${snapshot.objectivesTotal}`}
          />
          <SnapshotStat label="Exam result" value={String(snapshot.examPercent)} unit="%" />
          <SnapshotStat
            label="Project status"
            value={snapshot.projectStatusLabel}
            tone={snapshot.projectStatusTone}
          />
          <SnapshotStat
            label="Hearing status"
            value={snapshot.hearingStatusLabel}
            tone={snapshot.hearingStatusTone}
          />
        </div>
      </div>

      <button
        type="button"
        onClick={onContinue}
        className="relative flex min-h-32 cursor-pointer items-end overflow-hidden border-0 p-0 text-left sm:w-52 sm:shrink-0"
      >
        <img
          src={course.image?.url}
          alt=""
          aria-hidden
          className="absolute inset-0 h-full w-full object-cover"
        />
        <span
          aria-hidden
          className="absolute inset-0 bg-linear-to-b from-[#001c28]/0 via-[#001c28]/20 to-[#001c28]"
        />
        <span className="relative flex w-full items-center justify-between gap-2 px-6 py-5 text-(--primary-O100)">
          <span className="l4 semibold">Continue learning</span>
          <ArrowRight className="size-4.5" />
        </span>
      </button>
    </div>
  );
};
